import { TextField, Tooltip, Typography, Box, IconButton } from '@mui/material';
import { ClassNames } from '@emotion/react';
import PropTypes from 'prop-types';
import {
  ComboBox,
  FreeSolo,
  MultiSelect,
  ComboBoxCreatableExpert,
  ComboBoxCreatableOutlet,
  FileTableWithDragAndDrop,
  SingleSelectLinkedService,
  UrlTableWithDialog,
  ComboBoxCreatableDepartment,
  ComboBoxCreatableCommunityMember,
  ComboBoxAudience,
  ComboBoxCreatableJournalist,
  MultiSelectOrganizations,
} from '../Cells/CellTypes';
import {
  ContentLogKeys,
  DisplayKeys,
  EngagementKeys,
  IssueAndServiceSharedValues,
  ServiceKeys,
} from '../../shared/constants/IssueAndServiceKeys';
import { useSelector } from 'react-redux';
import { ContactMethodName, diversityOptions, TrackerDataType } from '../../shared/constants/constants';
import UrlDialog from '../ServiceLog/UrlDialog';
import { useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/AddBox';
import AddLinkIcon from '@mui/icons-material/AddLink';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import DatePickerInputField from '../../shared/inputs/DatePickerInputField';
import StatusInputSelection from '../../shared/inputs/StatusInputSelection';
import ComplexityInputSelection from '../../shared/inputs/ComplexityInputSelection';
import ToneInputSelection from '../../shared/inputs/ToneInputSelection';
import { TYPE_TOOLTIP_PLACEHOLDER } from '../../shared/constants/tableTooltips';
import LiveLinkDialog from './LiveLinkDialog';

const UrlUploadButton = ({ setFormOpen, disabled = false }) => {
  return (
    <Tooltip title="add link to web-hosted file">
      <IconButton
        disabled={disabled}
        color="primary"
        onClick={() => setFormOpen(true)}
        size="large"
        data-cy="modal-url-upload-button"
      >
        <AddLinkIcon />
      </IconButton>
    </Tooltip>
  );
};

UrlUploadButton.propTypes = {
  setFormOpen: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

function QueryAdderCell(props) {
  const {
    col,
    fields,
    dropdownTable,
    callback,
    className,
    required,
    error,
    eventHandler,
    files,
    handleDrag,
    handleDrop,
    isDragActive,
    isUpdating = false,
    isBulkUpdate = false,
  } = props;
  let options = [];
  const currentUser = useSelector((state) => state.user.userInfo);
  const dropdowns = dropdownTable || {};
  const users = dropdowns['lead'] || [];
  const journalists = dropdowns['journalist'] || [];
  const communityMembers = dropdowns['communityMember'] || [];
  const audiences = dropdowns['audience'] || [];
  const experts = dropdowns['expert'] || [];
  const topicOptionsByType = {
    [TrackerDataType.MEDIA_INTERACTION]: dropdowns['topic'] || [],
    [TrackerDataType.ENGAGEMENT]: dropdowns['engagementTopic'] || [],
    [TrackerDataType.CONTENT_LOG]: dropdowns['contentLogTopic'] || [],
  };
  const topics = topicOptionsByType[eventHandler?.type] || dropdowns['topic'] || [];
  const orgAgents = dropdowns['orgAgents'] || {};
  const units = dropdowns['units'] || [];
  const preSelectUser = [
    {
      _id: currentUser.id,
      name: currentUser.name,
    },
  ];
  const [isUrlFormOpen, setIsUrlFormOpen] = useState(false);
  const [isLiveLinkDialogOpen, setIsLiveLinkDialogOpen] = useState(false);
  const [editingLiveLink, setEditingLiveLink] = useState(null);
  const file = useRef(null);

  const FileUploadButton = ({ onChange, colName }) => {
    const handleFileSubmit = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.target.files.length) {
        onChange([...files, ...e.target.files], colName);
      }
    };
    return (
      <>
        <input type="file" multiple id="file" ref={file} onChange={handleFileSubmit} style={{ display: 'none' }} />
        <Tooltip title="add file(s)">
          <label htmlFor="file" data-cy="modal-file-upload-button">
            <IconButton color="primary" onClick={() => file.current.click()} size="large">
              <AddIcon />
            </IconButton>
          </label>
        </Tooltip>
      </>
    );
  };

  const handleSubmit = (callback, dialogValue, handleClose, colName) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    callback([...files, { ...dialogValue, type: 'url' }], colName);

    handleClose();
  };

  return (
    <ClassNames>
      {({ css }) => {
        const classes = {
          mediumCell: css({
            display: 'flex',
            flexDirection: 'column',
            width: '49%',
          }),
          smallCell: css({
            display: 'flex',
            flexDirection: 'column',
            width: '23.5%',
          }),
          bigCell: css({
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }),
          datePicker: css({
            height: '56px',
            width: '100%',
          }),
          datePickerInput: css({
            height: '56px',
            width: '100%',
          }),
          commsCell: css({
            flex: '1 0 0',
            minWidth: '100%',
            display: 'block',
            fontWeight: 400,
          }),
          dateLabel: css({
            flex: '0 0 auto',
            maxWidth: '38%',
            minWidth: 190,
            marginBottom: '15px',
          }),
          label: css({
            flex: '0 0 auto',
            maxWidth: '100%',
            marginBottom: '15px',
          }),
          labelError: css({
            color: '#d32f2f',
          }),
          topicLabel: css({
            fontWeight: 'bold',
            fontSize: 'large',
          }),
        };

        const renderInputLabel = (className, labelText, required, error) => {
          return (
            <label htmlFor={labelText} className={`${className} ${error ? classes.labelError : ''}`}>
              {`${labelText} ${required ? '*' : ''}`}
            </label>
          );
        };

        let cell = (
          <label className={classes.label} htmlFor={DisplayKeys[col] || col}>
            <TextField
              align="left"
              className={`${className}`}
              InputLabelProps={{
                className: `${col === 'service' ? classes.topicLabel : ''}`,
              }}
              defaultValue={fields[col]}
              value={fields[col] ? fields[col] : ''}
              label={DisplayKeys[col] || col}
              onChange={(e) => {
                callback(e.target.value, col);
              }}
              required={required}
              error={error}
              variant="standard"
              data-cy={`${col}-input-QueryAdderCell`}
            />
          </label>
        );

        //TODO change to use CellSelector after backend key adjustments are removed.
        switch (col) {
          case 'date':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.dateLabel, DisplayKeys[col] || col, required, error)}
                <DatePickerInputField
                  classes={{
                    textInputFieldContainerClass: classes.datePicker,
                    textInputFieldClass: classes.datePickerInput,
                  }}
                  selected={fields[col] || new Date()}
                  onChange={(date) => {
                    callback(date, col);
                  }}
                  required={required}
                  data-cy="datePicker-QueryAdderCell"
                />
              </Box>
            );
            break;
          case 'entryDate': {
            let selected = fields[col];
            if (!isBulkUpdate && !selected) {
              selected = new Date();
            }
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.dateLabel, DisplayKeys[col] || col, required, error)}
                <DatePickerInputField
                  classes={{
                    textInputFieldContainerClass: classes.datePicker,
                    textInputFieldClass: classes.datePickerInput,
                  }}
                  selected={selected}
                  onChange={(date) => {
                    callback(date, col);
                  }}
                  onClear={() => callback(null, col)}
                  required={required}
                  data-cy="datePicker-QueryAdderCell-entryDate"
                />
              </Box>
            );
            break;
          }
          case 'publishDate':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.dateLabel, DisplayKeys[col] || col, required, error)}
                <DatePickerInputField
                  classes={{
                    textInputFieldContainerClass: classes.datePicker,
                    textInputFieldClass: classes.datePickerInput,
                  }}
                  selected={fields[col]}
                  onChange={(date) => {
                    callback(date, col);
                  }}
                  required={required}
                  data-cy="datePicker-QueryAdderCell-publishDate"
                />
              </Box>
            );
            break;
          case 'dueDate':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.dateLabel, DisplayKeys[col] || col, required, error)}
                <DatePickerInputField
                  classes={{
                    textInputFieldContainerClass: classes.datePicker,
                    textInputFieldClass: classes.datePickerInput,
                  }}
                  selected={fields[col]}
                  onChange={(date) => {
                    callback(date, col);
                  }}
                  onClear={() => callback(null, col)}
                  required={required}
                  data-cy="datePicker-QueryAdderCell-dueDate"
                />
              </Box>
            );
            break;
          case 'service':
            cell = (
              <Box className={classes.bigCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <TextField
                  align="left"
                  className={`${className}`}
                  InputLabelProps={{
                    className: `${col === 'service' ? classes.topicLabel : ''}`,
                  }}
                  defaultValue={fields[col]}
                  value={fields[col] ? fields[col] : ''}
                  onChange={(e) => {
                    callback(e.target.value, col);
                  }}
                  required={required}
                  error={error}
                  variant="outlined"
                  data-cy="service-input-QueryAdderCell"
                />
              </Box>
            );
            break;
          case 'topic':
            cell = (
              <Box className={classes.bigCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <FreeSolo
                  list={topics}
                  callback={callback}
                  col={col}
                  className={`${className} ${classes.topicLabel}`}
                  required={required}
                  value={fields[col]}
                  error={error}
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          case 'otherNotes':
            cell = (
              <Box className={classes.bigCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <FreeSolo
                  list={dropdowns[col] || []}
                  callback={callback}
                  col={col}
                  className={className}
                  required={required}
                  value={fields[col]}
                  error={error}
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          case 'tone':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <ToneInputSelection
                  selectedTones={fields[col] ? [fields[col]] : []}
                  onSelect={(selectedTones) => callback(selectedTones.length > 0 ? selectedTones[0] : undefined, col)}
                />
              </Box>
            );
            break;
          case 'lead':
            if (!fields[col] && !isBulkUpdate) {
              fields[col] = preSelectUser;
            }
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <MultiSelect
                  options={users}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  data-cy="lead-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          case IssueAndServiceSharedValues.leadExpert:
          case 'Expert Required':
          case 'unitLead':
          case 'expert':
            options = fields[col];
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <ComboBoxCreatableExpert
                  units={units}
                  options={experts.filter((expert) => !expert.archived)}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  setVal={(v) => (fields[col] = v)}
                  className={className}
                  required={required}
                  error={error}
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          case 'teamMember': {
            if (!fields[col] && !isBulkUpdate) {
              fields[col] = preSelectUser;
            }

            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <MultiSelect
                  options={users}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  data-cy="teamMember-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          }
          case 'outlet':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <ComboBoxCreatableOutlet
                  options={(dropdowns['outlet'] || []).filter((outlet) => !outlet.archived)}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  setVal={(v) => (fields[col] = v)}
                  className={className}
                  required={required}
                  error={error}
                  data-cy="outlet-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          case 'organization':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <MultiSelectOrganizations
                  options={(dropdowns['organization'] || []).filter((organization) => !organization.archived)}
                  callback={callback}
                  val={Array.isArray(fields[col]) ? fields[col] : []}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          case 'journalist':
            options = fields['outlet']
              ? orgAgents[fields['outlet']]?.filter((journalist) => !journalist.archived)
              : journalists.filter((journalist) => !journalist.archived);

            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <ComboBoxCreatableJournalist
                  orgs={dropdowns['outlet'] || []}
                  options={options}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  selectedOrg={fields['outlet']}
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          case 'communityMember': {
            let selectedOrganizationIds = [];
            if (Array.isArray(fields['organization'])) {
              selectedOrganizationIds = fields['organization']
                .map((organization) => organization?._id ?? organization)
                .filter(Boolean);
            }

            let scopedOptions;
            if (selectedOrganizationIds.length > 0) {
              scopedOptions = communityMembers
                .filter((communityMember) => !communityMember.archived)
                .filter((communityMember) => {
                  const organizationRef = communityMember?.Organization;

                  let organizationId = null;
                  if (typeof organizationRef === 'string') {
                    organizationId = organizationRef;
                  } else if (organizationRef?._id) {
                    organizationId = organizationRef._id;
                  } else if (organizationRef?.id) {
                    organizationId = organizationRef.id;
                  }

                  if (!organizationId) {
                    return false;
                  }

                  return selectedOrganizationIds.includes(organizationId);
                });
            } else {
              scopedOptions = communityMembers.filter((communityMember) => !communityMember.archived);
            }

            const currentCommunityMembers = Array.isArray(fields[col]) ? fields[col] : [];
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <ComboBoxCreatableCommunityMember
                  orgs={dropdowns['organization'] || []}
                  options={scopedOptions}
                  callback={callback}
                  val={currentCommunityMembers}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  selectedOrg={fields['organization']}
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          }
          case 'audience': {
            const currentAudiences = Array.isArray(fields[col]) ? fields[col] : [];
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <ComboBoxAudience
                  options={audiences.filter((audience) => !audience.archived)}
                  callback={callback}
                  val={currentAudiences}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          }
          case 'dept':
            // const depts = dropdownTable['dept'];
            // alert(JSON.stringify(depts));
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <ComboBoxCreatableDepartment
                  options={(dropdowns['dept'] || []).filter((dept) => !dept.archived)}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  data-cy="dept-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          case 'campaign': {
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <MultiSelect
                  options={(dropdowns['campaigns'] || []).filter((campaign) => !campaign.archived)}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  groupBy={(option) => option.campaigns}
                  renderOptionWithTooltip
                  disableTooltip
                  required={required}
                  error={error}
                  data-cy="campaign-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          }
          case 'units': {
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <MultiSelect
                  options={(dropdowns['units'] || []).filter((unit) => !unit.archived).map((unit) => unit.name)}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  required={required}
                  error={error}
                  groupBy={(option) => option.units}
                  data-cy="units-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          }
          case 'type':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <MultiSelect
                  options={(dropdowns['type'] || [])
                    .filter((type) => !type.archived)
                    .sort((a, b) => -b?.type?.localeCompare(a?.type) || -b?.firstLetter?.localeCompare(a?.firstLetter))}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  groupBy={(option) => option.type}
                  renderOptionWithTooltip
                  tooltipPlaceholder={TYPE_TOOLTIP_PLACEHOLDER}
                  disableTooltip
                  required={required}
                  error={error}
                  data-cy="type-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          case 'linkedService':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <SingleSelectLinkedService
                  options={dropdowns['service'] || []}
                  callback={callback}
                  val={fields[col]?.length > 0 ? fields[col][0] : ''}
                  col={col}
                  required={required}
                  error={error}
                  data-cy="linkedService-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          case 'complexity':
            cell = (
              <Box className={classes.smallCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <ComplexityInputSelection
                  selectedComplexities={fields[col] ? [fields[col]] : []}
                  onSelect={(selectedComplexities) =>
                    callback(selectedComplexities.length > 0 ? selectedComplexities[0] : undefined, col)
                  }
                />
              </Box>
            );
            break;
          case 'diversity':
            cell = (
              <Box className={classes.smallCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <ComboBox
                  options={diversityOptions}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  required={required}
                  error={error}
                  data-cy="diversity-comboBox-QueryAdderCell"
                  isConstant
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          case 'contactMethod': {
            const contactMethodLabelMap = {
              [ContactMethodName.NO_INTERMEDIARY]: 'in person',
              [ContactMethodName.PHONE]: 'phone call',
            };

            const getContactMethodLabel = (option) => {
              const name = option?.name ?? '';
              return contactMethodLabelMap[name] || name;
            };

            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, 'Mode of Communication', required, error)}
                <ComboBox
                  options={dropdowns['contactMethod'] || []}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  data-cy="contactMethod-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                  getOptionLabel={getContactMethodLabel}
                  disablePortal={false}
                />
              </Box>
            );
            break;
          }
          case 'linkedInteraction':
          case 'linkedEngagement':
            cell = <></>;
            break;
          case 'file': {
            let fileLabel = ContentLogKeys.file;
            if (eventHandler.type === TrackerDataType.SERVICE) {
              fileLabel = ServiceKeys.file;
            } else if (eventHandler.type === TrackerDataType.ENGAGEMENT) {
              fileLabel = EngagementKeys.file;
            }

            cell =
              eventHandler.type === TrackerDataType.SERVICE ||
              eventHandler.type === TrackerDataType.ENGAGEMENT ||
              eventHandler.type === TrackerDataType.CONTENT_LOG ? (
                <Box style={{ minHeight: '5vh' }} className={classes.commsCell}>
                  <UrlDialog
                    colName={col}
                    type={eventHandler.type}
                    isOpen={isUrlFormOpen}
                    setIsOpen={setIsUrlFormOpen}
                    callback={callback}
                    handleSubmit={handleSubmit}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <Typography>{fileLabel}</Typography>
                    <FileUploadButton file={file} onChange={callback} colName={col} />
                    <UrlUploadButton setFormOpen={setIsUrlFormOpen} />
                  </Box>
                  <FileTableWithDragAndDrop
                    col={col}
                    files={files}
                    onDelete={callback}
                    handleDrag={handleDrag}
                    handleDrop={handleDrop}
                    isDragActive={isDragActive}
                    isUpdating={isUpdating}
                  />
                </Box>
              ) : (
                <UrlTableWithDialog col={col} files={files} onChange={callback} />
              );
            break;
          }
          case 'liveLink': {
            const currentLink = fields[col] ? [{ ...fields[col], type: 'url' }] : [];
            cell = (
              <Box style={{ minHeight: '5vh' }} className={classes.commsCell}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Typography>{ContentLogKeys.liveLink}</Typography>
                  <UrlUploadButton
                    disabled={currentLink.length > 0}
                    setFormOpen={() => {
                      setEditingLiveLink(null);
                      setIsLiveLinkDialogOpen(true);
                    }}
                  />
                </Box>
                <FileTableWithDragAndDrop
                  col={col}
                  files={currentLink}
                  onDelete={() => callback(null, col)}
                  isUpdating={isUpdating}
                  onEditItem={(fileToEdit) => {
                    setEditingLiveLink(fileToEdit);
                    setIsLiveLinkDialogOpen(true);
                  }}
                />
                <LiveLinkDialog
                  open={isLiveLinkDialogOpen}
                  setOpen={setIsLiveLinkDialogOpen}
                  initialValue={editingLiveLink}
                  onSubmit={(value) => callback(value, col)}
                  onDelete={() => callback(null, col)}
                />
              </Box>
            );
            break;
          }
          case 'actions':
          case 'response':
          case 'background':
          case 'content':
          case 'notes':
          case 'keyMessaging':
            cell = (
              <Box className={classes.bigCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <RichTextEditor
                  defaultValue={''}
                  value={fields[col]}
                  onChange={(value) => {
                    callback(value, col);
                  }}
                  required={required}
                  error={error}
                  dataCy={`${col}-input-QueryAdderCell`}
                  readOnly={false}
                />
              </Box>
            );
            break;
          case 'status':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <StatusInputSelection
                  options={(dropdowns[col] || []).map((item) => item.name)}
                  selectedStatus={fields[col] ? [fields[col]] : []}
                  onSelect={(selectedStatus) => {
                    callback(selectedStatus.length > 0 ? selectedStatus[0] : undefined, col);
                  }}
                  includeInApplicationServiceOrIssueStatus={false}
                />
              </Box>
            );
            break;
          default:
            if (col !== 'service' && Object.prototype.hasOwnProperty.call(dropdowns, col)) {
              cell = (
                <Box className={classes.mediumCell}>
                  {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                  <ComboBox
                    options={dropdowns[col]}
                    callback={callback}
                    val={fields[col]}
                    col={col}
                    className={className}
                    required={required}
                    error={error}
                    data-cy={`${col}-select-QueryAdderCell`}
                    textFieldVariant="outlined"
                    displayLabel={false}
                  />
                </Box>
              );
            }
            break;
        }

        return <>{cell}</>;
      }}
    </ClassNames>
  );
}

QueryAdderCell.propTypes = {
  col: PropTypes.string.isRequired,
  fields: PropTypes.object.isRequired,
  dropdownTable: PropTypes.shape({
    lead: PropTypes.array,
    journalist: PropTypes.array,
    communityMember: PropTypes.array,
    audience: PropTypes.array,
    expert: PropTypes.array,
    topic: PropTypes.array,
    engagementTopic: PropTypes.array,
    contentLogTopic: PropTypes.array,
    orgAgents: PropTypes.object,
    units: PropTypes.array,
    outlet: PropTypes.array,
    organization: PropTypes.array,
    dept: PropTypes.array,
    campaigns: PropTypes.array,
    type: PropTypes.array,
    service: PropTypes.array,
    contactMethod: PropTypes.array,
    status: PropTypes.array,
  }),
  callback: PropTypes.func.isRequired,
  className: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.bool,
  eventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
  }),
  files: PropTypes.array,
  handleDrag: PropTypes.func,
  handleDrop: PropTypes.func,
  isDragActive: PropTypes.bool,
  isUpdating: PropTypes.bool,
  isBulkUpdate: PropTypes.bool,
};

export { QueryAdderCell };
